import type {
  Catalog,
  ExpenseCategoryDefinition,
  ExpenseEntry,
  ExpenseStatus,
  ExpenseTypeDefinition,
} from "@/api/types";

/** Names as a list that reads well, "Gary, Matt, Wesley", however they were typed. */
export function nameList(names: string): string {
  return names
    .split(/[,;\n]/)
    .map((name) => name.trim())
    .filter(Boolean)
    .join(", ");
}

export function money(amount: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

export function miles(value: number): string {
  return `${value.toLocaleString("en-US", { maximumFractionDigits: 1 })} mi`;
}

/** Today as the yyyy-mm-dd the API takes for a DateOnly, in the phone's own time zone. */
export function todayIso(now = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${day}`;
}

// A DateOnly is a calendar day, not an instant. Parsing "2026-10-05" with new Date() reads it as
// UTC midnight, which is the evening before anywhere west of Greenwich.
function calendarDay(iso: string): Date {
  const [year = 0, month = 1, day = 1] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
}

const monthDay = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const monthDayYear = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

/** "Oct 5" */
export function shortDate(iso: string): string {
  return monthDay.format(calendarDay(iso));
}

/** "Oct 5, 2026" */
export function longDate(iso: string): string {
  return monthDayYear.format(calendarDay(iso));
}

/** "Oct 5–7, 2026", "Sep 29 – Oct 2, 2026", or one day when they match. */
export function dateRange(startIso: string, endIso: string): string {
  const start = calendarDay(startIso);
  const end = calendarDay(endIso);
  if (start.getTime() === end.getTime()) return monthDayYear.format(start);

  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear && start.getMonth() === end.getMonth()) {
    return `${monthDay.format(start)}–${String(end.getDate())}, ${String(end.getFullYear())}`;
  }
  if (sameYear) {
    return `${monthDay.format(start)} – ${monthDay.format(end)}, ${String(end.getFullYear())}`;
  }
  return `${monthDayYear.format(start)} – ${monthDayYear.format(end)}`;
}

/** "Just now", "5 min ago", "3 hr ago", "Yesterday", then a date. */
export function relativeTime(isoInstant: string, now = new Date()): string {
  const then = new Date(isoInstant);
  const minutes = Math.round((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24 && then.getDate() === now.getDate()) return `${String(hours)} hr ago`;

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (
    then.getFullYear() === yesterday.getFullYear() &&
    then.getMonth() === yesterday.getMonth() &&
    then.getDate() === yesterday.getDate()
  ) {
    return "Yesterday";
  }
  return then.getFullYear() === now.getFullYear()
    ? monthDay.format(then)
    : monthDayYear.format(then);
}

/**
 * A report that was sent back is a draft again on the server, but it keeps the time it was first
 * submitted. That is the only thing telling it apart from one that was never sent.
 */
export function isSentBack(report: { status: ExpenseStatus; submittedAt: string | null }): boolean {
  return report.status === "Draft" && report.submittedAt !== null;
}

export function statusLabel(status: string, sentBack = false): string {
  switch (status) {
    case "Draft":
      return sentBack ? "Sent back" : "Draft";
    case "Submitted":
      return "With manager";
    case "ManagerApproved":
    case "AccountingApproved":
    case "ExecutiveApproved":
      return "With payroll";
    case "ExecutiveReview":
      return "With executive";
    case "DelegatorReview":
      return "To review";
    case "Paid":
      return "Paid";
    case "Rejected":
      return "Sent back";
    default:
      return status;
  }
}

export type StatusTone = "neutral" | "progress" | "danger" | "warn" | "ok";

export function statusTone(status: ExpenseStatus, sentBack = false): StatusTone {
  switch (status) {
    case "Draft":
      return sentBack ? "danger" : "neutral";
    case "Rejected":
      return "danger";
    case "Submitted":
    case "ManagerApproved":
    case "AccountingApproved":
    case "ExecutiveApproved":
      return "progress";
    case "ExecutiveReview":
    case "DelegatorReview":
      return "warn";
    case "Paid":
      return "ok";
  }
}

const categoryNames: Record<string, string> = {
  Meals: "Meals",
  Lodging: "Lodging",
  Airfare: "Airfare",
  GroundTransport: "Ground transport",
  Supplies: "Supplies",
  Other: "Other",
};

export function categoryLabel(category: string | null): string {
  if (!category) return "Uncategorised";
  return categoryNames[category] ?? category.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function findType(
  catalog: Catalog | undefined,
  typeId: string | null | undefined,
): { category: ExpenseCategoryDefinition; type: ExpenseTypeDefinition } | undefined {
  if (!catalog || !typeId) return undefined;
  for (const category of catalog.categories) {
    const type = category.types.find((t) => t.id === typeId);
    if (type) return { category, type };
  }
  return undefined;
}

/** What an expense is, as its type reads: "Team Meal/Outing", or "Travel · Other". */
export function entryTypeLabel(
  entry: Pick<ExpenseEntry, "expenseType" | "category">,
  catalog: Catalog | undefined,
): string {
  const found = findType(catalog, entry.expenseType);
  if (found) {
    return found.type.name === "Other" ? `${found.category.name} · Other` : found.type.name;
  }
  const category = catalog?.categories.find((c) => c.id === entry.category);
  return category ? category.name : categoryLabel(entry.category);
}

const AddedVia: Record<string, string> = {
  Photo: "Snapped a photo",
  Paste: "Pasted",
  Upload: "Uploaded",
  Drop: "Dragged in",
  Manual: "Entered manually",
  Mileage: "Entered mileage",
  Email: "Emailed",
  Moved: "Moved from another report",
};

/** How an expense was added, "Snapped a photo on phone", or null if that wasn't recorded. */
export function addedHow(via: string | null, device: string | null): string | null {
  const how = via ? AddedVia[via] : undefined;
  if (!how) return null;
  return device ? `${how} on ${device.toLowerCase()}` : how;
}

/** History lines read as sentences: "Added Hotel Edison", "Sent back by manager". */
export function eventLabel(kind: string, comment: string | null): string {
  switch (kind) {
    case "Created":
      return "Created the report";
    case "Updated":
      return "Edited the report details";
    case "EntryAdded":
      return comment ? `Added ${comment}` : "Added an expense";
    case "EntryUpdated":
      return comment ? `Edited ${comment}` : "Edited an expense";
    case "EntryRemoved":
      return comment ? `Removed ${comment}` : "Removed an expense";
    case "Submitted":
      return "Submitted";
    case "ManagerApproved":
      return "Approved";
    case "ManagerRejected":
      return "Sent back";
    case "AccountingApproved":
      return "Approved for payment";
    case "AccountingRejected":
      return "Sent back by accounting";
    case "PayrollRejected":
      return "Sent back by payroll";
    case "ExecutiveRequested":
      return comment ? `Sent for executive approval: ${comment}` : "Sent for executive approval";
    case "ExecutiveApproved":
      return "Approved by executive";
    case "ExecutiveDeclined":
      return "Declined by executive";
    case "MarkedPaid":
      return comment && /^\d{4}-\d{2}-\d{2}/.test(comment)
        ? `Paid, reimbursement on ${shortDate(comment.slice(0, 10))}`
        : "Paid";
    case "Reopened":
      return "Re-opened by payroll";
    case "DelegatorApproved":
      return "Approved what was prepared";
    case "DelegatorRejected":
      return "Sent back to their delegate";
    default:
      return kind.replace(/([a-z])([A-Z])/g, "$1 $2");
  }
}

/**
 * The words someone wrote with an event, shown under it: a reviewer's note, not the name of an
 * expense. A payment's comment starts with its date, which the label already says.
 */
export function eventNote(kind: string, comment: string | null): string | null {
  if (!comment || kind.startsWith("Entry")) return null;
  if (kind === "MarkedPaid")
    return /^\d{4}-\d{2}-\d{2}/.test(comment) ? comment.slice(10).trim() || null : comment;
  return comment;
}

/** "R&D MTP - 30720", or just the name when the office directory has no code for it. */
export function departmentLabel(report: {
  department?: string | null;
  departmentCode?: string | null;
}): string | null {
  if (!report.department) return null;
  return report.departmentCode
    ? `${report.department} - ${report.departmentCode}`
    : report.department;
}
